import React, { useState } from 'react';
import { Table, Button, Badge, Card, Spinner } from 'react-bootstrap';
import useVoucher from '../../hooks/useVoucher';
import VoucherUsageModal from '../VoucherUsageModal/VoucherUsageModal';
import CustomModal from '../CustomModal/CustomModal';
import AssegnaVoucherAgenziaModal from '../AssegnaVoucherAgenziaModal/AssegnaVoucherAgenziaModal';
import useMediaQuery from '../../hooks/useMediaQuery';
import { alertService } from '../../services/AlertService';

export default function VisualizzaVoucherAgenzia({ voucherList, idAgenzia, onRefresh }) {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const { getVoucherUsage, loadingStateVoucher } = useVoucher();

    const [showUsageModal, setShowUsageModal] = useState(false);
    const [usageData, setUsageData] = useState([]);
    const [isUsageLoading, setIsUsageLoading] = useState(false);
    const [selectedVoucherForUsage, setSelectedVoucherForUsage] = useState(null);
    const [showNoteModal, setShowNoteModal] = useState(false);
    const [noteContent, setNoteContent] = useState('');
    const [showAssegnaModal, setShowAssegnaModal] = useState(false);
    const [selectedVoucherForAssignment, setSelectedVoucherForAssignment] = useState(null);

    const handleShowUsage = async (voucher) => {
        setSelectedVoucherForUsage(voucher);
        setIsUsageLoading(true);
        setShowUsageModal(true);
        try {
            const usageResult = await getVoucherUsage(voucher.id_utenti_voucher);
            setUsageData(Array.isArray(usageResult) ? usageResult : (usageResult?.data || []));
        } catch (error) {
            setShowUsageModal(false);
            // alertService.error("Errore nel caricamento degli utilizzi.");
        } finally {
            setIsUsageLoading(false);
        }
    };

    const handleCloseUsageModal = () => setShowUsageModal(false);
    const handleShowNoteModal = (voucher) => {
        setNoteContent(voucher.note || '');
        setShowNoteModal(true);
    };
    const handleShowAssegnaModal = (voucher) => {
        setSelectedVoucherForAssignment(voucher);
        setShowAssegnaModal(true);
    };

    const isVoucherAvailable = (voucher) => {
        const isExpired = new Date(voucher.data_scadenza) < new Date();
        const isEsaurito = voucher.esaurito === 1 || voucher.esaurito === "1";
        const qtResidua = (voucher.qt_disponibile || 0) - (voucher.qt_utilizzata || 0);
        return !isExpired && !isEsaurito && qtResidua > 0;
    };
    const handleCloseAssegnaModal = () => {
        setShowAssegnaModal(false);
        setSelectedVoucherForAssignment(null);
    };
    const handleAssegnaSuccess = () => {
        if (onRefresh) {
            onRefresh();
        }
    };

    const renderVoucherStatus = (voucher) => {
        const isExpired = new Date(voucher.data_scadenza) < new Date();
        if (voucher.esaurito === 1 || voucher.esaurito === "1") {
            return <Badge bg="danger">Esaurito</Badge>;
        }
        if (isExpired) {
            return <Badge bg="warning" text="dark">Scaduto</Badge>;
        }
        return <Badge bg="success">Attivo</Badge>;
    };

    if (!voucherList || voucherList.length === 0) {
        return (
            <Card className="p-4">
                <div className="text-center">
                    <p className="text-muted">Nessun pacchetto voucher assegnato alla tua agenzia.</p>
                </div>
            </Card>
        );
    }

    // Ordina per data di acquisto (più recenti prima)
    const sortedVouchers = [...voucherList].sort((a, b) => {
        const dateA = new Date(b.data_acquisto || b.data_scadenza);
        const dateB = new Date(a.data_acquisto || a.data_scadenza);
        return dateA - dateB;
    });

    return (
        <>
            {isMobile ? (
                <div className="d-flex flex-column gap-3">
                    {sortedVouchers.map((v) => {
                        const qtResidua = (v.qt_disponibile || 0) - (v.qt_utilizzata || 0);
                        const isAvailable = isVoucherAvailable(v);
                        return (
                            <Card key={v.id_utenti_voucher || v.id_corso} className="shadow-sm">
                                <Card.Body>
                                    <div className="d-flex justify-content-between mb-2">
                                        <h6 className="mb-0">{v.nome_corso}</h6>
                                        {renderVoucherStatus(v)}
                                    </div>
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                        <Badge bg="info">Disp: {v.qt_disponibile || 0}</Badge>
                                        <Badge bg="dark">Usati: {v.qt_utilizzata || 0}</Badge>
                                        <Badge bg="secondary" text="dark">Residui: {qtResidua}</Badge>
                                    </div>
                                    <small className="text-muted d-block mb-2">
                                        Scade il: {new Date(v.data_scadenza).toLocaleDateString('it-IT')}
                                    </small>
                                    <div className="d-flex flex-wrap gap-2">
                                        <Button 
                                            variant="success" 
                                            size="sm" 
                                            onClick={() => isAvailable && handleShowAssegnaModal(v)}
                                            title={isAvailable ? "Assegna Voucher" : "Voucher non disponibile (scaduto o esaurito)"}
                                            style={!isAvailable ? { 
                                                backgroundColor: '#f5f5f5', 
                                                borderColor: '#d0d0d0', 
                                                color: '#6c757d',
                                                cursor: 'not-allowed'
                                            } : {}}
                                        >
                                            <i className="bi bi-ticket-perforated me-1"></i> Assegna
                                        </Button>
                                        <Button 
                                            variant="outline-primary" 
                                            size="sm" 
                                            onClick={() => (v.qt_utilizzata || 0) > 0 && handleShowUsage(v)}
                                            title="Utilizzi"
                                            style={(v.qt_utilizzata || 0) === 0 ? { 
                                                backgroundColor: '#f5f5f5', 
                                                borderColor: '#d0d0d0', 
                                                color: '#6c757d',
                                                cursor: 'not-allowed'
                                            } : {}}
                                        >
                                            <i className="bi bi-people-fill me-1"></i> Utilizzi
                                        </Button>
                                        {v.note && (
                                            <Button 
                                                variant="outline-info" 
                                                size="sm" 
                                                onClick={() => handleShowNoteModal(v)}
                                            >
                                                <i className="bi bi-info-circle-fill me-1"></i> Nota
                                            </Button>
                                        )}
                                    </div>
                                </Card.Body>
                            </Card>
                        );
                    })}
                </div>
            ) : (
                <div className="table-responsive">
                    <Table hover className="align-middle" style={{ tableLayout: 'fixed' }}>
                        <thead className="table-light">
                            <tr>
                                <th style={{ width: '30%' }}>Corso</th>
                                <th style={{ width: '10%' }}>Qt. Disp.</th>
                                <th style={{ width: '10%' }}>Qt. Util.</th>
                                <th style={{ width: '10%' }}>Qt. Residua</th>
                                <th style={{ width: '12%' }}>Scadenza</th>
                                <th style={{ width: '10%' }}>Stato</th>
                                <th style={{ width: '18%' }} className="text-end">Azioni</th>
                            </tr>
                        </thead>
                        <tbody>
                                    {sortedVouchers.map((v) => {
                                        const qtResidua = (v.qt_disponibile || 0) - (v.qt_utilizzata || 0);
                                        const isAvailable = isVoucherAvailable(v);
                                        return (
                                            <tr key={v.id_utenti_voucher || v.id_corso}>
                                                <td style={{ whiteSpace: 'normal', wordWrap: 'break-word' }}>
                                                    {v.nome_corso}
                                                </td>
                                                <td>
                                                    <Badge bg="info">{v.qt_disponibile || 0}</Badge>
                                                </td>
                                                <td>
                                                    <Badge bg="dark">{v.qt_utilizzata || 0}</Badge>
                                                </td>
                                                <td>
                                                    <Badge bg="secondary" text="dark">{qtResidua}</Badge>
                                                </td>
                                                <td>{new Date(v.data_scadenza).toLocaleDateString('it-IT')}</td>
                                                <td>{renderVoucherStatus(v)}</td>
                                                <td className="text-end">
                                                    <Button 
                                                        variant="success" 
                                                        size="sm" 
                                                        className="me-2" 
                                                        onClick={() => isAvailable && handleShowAssegnaModal(v)} 
                                                        title={isAvailable ? "Assegna Voucher" : "Voucher non disponibile (scaduto o esaurito)"}
                                                        style={!isAvailable ? { 
                                                            backgroundColor: '#f5f5f5', 
                                                            borderColor: '#d0d0d0', 
                                                            color: '#6c757d',
                                                            cursor: 'not-allowed'
                                                        } : {}}
                                                    >
                                                        <i className="bi bi-ticket-perforated me-1"></i>
                                                        Assegna
                                                    </Button>
                                                    <Button 
                                                        variant="outline-primary" 
                                                        size="sm" 
                                                        className="me-2" 
                                                        onClick={() => (v.qt_utilizzata || 0) > 0 && handleShowUsage(v)} 
                                                        title="Utilizzi"
                                                        style={(v.qt_utilizzata || 0) === 0 ? { 
                                                            backgroundColor: '#f5f5f5', 
                                                            borderColor: '#d0d0d0', 
                                                            color: '#6c757d',
                                                            cursor: 'not-allowed'
                                                        } : {}}
                                                    >
                                                        <i className="bi bi-people-fill"></i>
                                                    </Button>
                                            {v.note && (
                                                <Button 
                                                    variant="outline-info" 
                                                    size="sm" 
                                                    className="me-2" 
                                                    onClick={() => handleShowNoteModal(v)} 
                                                    title="Nota"
                                                >
                                                    <i className="bi bi-info-circle-fill"></i>
                                                </Button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            )}

            <VoucherUsageModal 
                show={showUsageModal} 
                onHide={handleCloseUsageModal} 
                isLoading={isUsageLoading} 
                data={usageData} 
                voucher={selectedVoucherForUsage}
            />
            
            <CustomModal 
                show={showNoteModal} 
                onHide={() => setShowNoteModal(false)} 
                onUndoChanges={() => setShowNoteModal(false)} 
                headerText="Nota Voucher" 
                size="md" 
                hideSaveBtn={true} 
                body={
                    <div className="p-3">
                        {noteContent || "Nessuna nota."}
                    </div>
                }
            />

            <AssegnaVoucherAgenziaModal
                voucher={selectedVoucherForAssignment}
                idAgenzia={idAgenzia}
                show={showAssegnaModal}
                onHide={handleCloseAssegnaModal}
                onSuccess={handleAssegnaSuccess}
            />
        </>
    );
}
